import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryProvider } from '@/contexts/QueryProvider';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AccessibilityProvider } from '@/contexts/AccessibilityContext';
import { ErrorBoundary, ToastProvider } from '@/components/ui';
import { OfflineIndicator } from '@/components/ui/OfflineIndicator';
import { SkipLink } from '@/components/ui/FocusManager';
import { Loading } from '@/components/ui/Loading';
import { initWebVitals, performanceMonitor } from '@/utils/performance';
import {
  loadPolyfills,
  getBrowserWarnings,
} from '@/utils/browserCompatibility';
import Layout from '@/components/Layout';

/**
 * SPRITE-1468: after a deploy the old hashed chunks are gone, so a tab that
 * still has the previous index.html fails to import the next page until a
 * reload. Reload once per URL on such a failure; a second failure on the same
 * URL is a real error and surfaces through the error boundary.
 */
const RELOAD_GUARD_KEY = 'admin:chunk-reload';

function reloadOnceForStaleChunk(): boolean {
  const current = window.location.href;
  let lastReloaded: string | null = null;
  try {
    lastReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch {
    // Storage blocked: fall through and surface the error instead.
  }
  if (lastReloaded === current) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, current);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** `React.lazy` that reloads once when the chunk is stale. */
function lazyWithReload<T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importer().then(
      (module) => {
        try {
          sessionStorage.removeItem(RELOAD_GUARD_KEY);
        } catch {
          // Nothing to clear.
        }
        return module;
      },
      (error: unknown) => {
        if (reloadOnceForStaleChunk()) {
          // The page is reloading; keep the fallback on screen meanwhile.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }
    )
  );
}

window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleChunk()) {
    event.preventDefault();
  }
});

const LoginPage = lazyWithReload(() => import('@/pages/LoginPage'));
const Dashboard = lazyWithReload(() => import('@/pages/Dashboard'));
const Users = lazyWithReload(() => import('@/pages/Users'));
const UserDetail = lazyWithReload(() => import('@/pages/UserDetail'));
const Organizations = lazyWithReload(() => import('@/pages/Organizations'));
const OrganizationDetail = lazyWithReload(
  () => import('@/pages/OrganizationDetail')
);
const Analytics = lazyWithReload(() => import('@/pages/Analytics'));
const CostMonitoring = lazyWithReload(() => import('@/pages/CostMonitoring'));
const Models = lazyWithReload(() => import('@/pages/Models'));
const AuditLogs = lazyWithReload(() => import('@/pages/AuditLogs'));
const Jobs = lazyWithReload(() => import('@/pages/Jobs'));
const Settings = lazyWithReload(() => import('@/pages/Settings'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen bg-gray-900 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Loading application"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <div className="text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const LazyWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const AppRoutes: React.FC = () => {
  return (
    <>
      {/* Skip links for keyboard navigation */}
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <SkipLink href="#navigation">Skip to navigation</SkipLink>

      <Routes>
        <Route
          path="/login"
          element={
            <LazyWrapper>
              <LoginPage />
            </LazyWrapper>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <LazyWrapper>
                <Dashboard />
              </LazyWrapper>
            }
          />
          <Route
            path="users"
            element={
              <LazyWrapper>
                <Users />
              </LazyWrapper>
            }
          />
          <Route
            path="users/:uid"
            element={
              <LazyWrapper>
                <UserDetail />
              </LazyWrapper>
            }
          />
          <Route
            path="organizations"
            element={
              <LazyWrapper>
                <Organizations />
              </LazyWrapper>
            }
          />
          <Route
            path="organizations/:orgId"
            element={
              <LazyWrapper>
                <OrganizationDetail />
              </LazyWrapper>
            }
          />
          <Route
            path="analytics"
            element={
              <LazyWrapper>
                <Analytics />
              </LazyWrapper>
            }
          />
          <Route
            path="cost-monitoring"
            element={
              <LazyWrapper>
                <CostMonitoring />
              </LazyWrapper>
            }
          />
          <Route
            path="audit-logs"
            element={
              <LazyWrapper>
                <AuditLogs />
              </LazyWrapper>
            }
          />
          <Route
            path="jobs"
            element={
              <LazyWrapper>
                <Jobs />
              </LazyWrapper>
            }
          />
          <Route
            path="settings"
            element={
              <LazyWrapper>
                <Settings />
              </LazyWrapper>
            }
          />
          <Route
            path="models"
            element={
              <LazyWrapper>
                <Models />
              </LazyWrapper>
            }
          />
        </Route>

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <OfflineIndicator />
    </>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await loadPolyfills();

        await initWebVitals();

        const warnings = getBrowserWarnings();
        if (warnings.length > 0) {
          console.warn('Browser compatibility warnings:', warnings);
        }

        if (import.meta.env.DEV) {
          // ESM bundle: `require` does not exist in the browser (it threw on
          // every dev load); load the dev-only checks with a dynamic import.
          setTimeout(() => {
            import('@/utils/performance').then(
              ({ analyzeBundleSize, checkPerformanceBudget }) => {
                analyzeBundleSize();
                checkPerformanceBudget();
              }
            );
          }, 2000);
        }
      } catch (error) {
        console.error('App initialization failed:', error);
      }
    };

    initializeApp();

    // Cleanup on unmount
    return () => {
      performanceMonitor.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AccessibilityProvider>
          <QueryProvider>
            <ToastProvider>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </ToastProvider>
          </QueryProvider>
        </AccessibilityProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
